/**
 * Specular button effect — Borie de Maurel, homepage only
 *
 * CSS/JS reimplementation of reactbits.dev's "Specular Button": a thin light
 * streak hugs the button's outer edge and swings to face the pointer,
 * brightening as the pointer nears the button and fading out when it's far.
 * The original uses a WebGL shader (ogl); this uses a masked conic-gradient
 * ring driven by CSS custom properties, which is enough to read the same
 * way at button scale without adding a WebGL dependency to the theme.
 *
 * MAJ thème du 16/10 : les boutons réglés avec des couleurs personnalisées
 * (« DÉCOUVRIR LES VINS », « LE DOMAINE ») portent désormais la classe
 * `button-custom` au lieu de `button` : elle est ajoutée au sélecteur.
 */
(function () {
  const main = document.querySelector('main[data-template="index"]');
  if (!main) return;

  const buttons = main.querySelectorAll('.button, .button-secondary, .button-custom');
  if (!buttons.length) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const IDLE_SPEED = 0.35; // rad/s, idle sweep before the pointer has moved
  const PROXIMITY = 220; // px from the button edge at which the shine starts fading in

  const instances = Array.from(buttons, (btn) => {
    btn.classList.add('bdm-specular');
    const fx = document.createElement('span');
    fx.className = 'bdm-specular__fx';
    fx.setAttribute('aria-hidden', 'true');
    btn.prepend(fx);
    return { btn, fx, angle: 2.4, idleAngle: 2.4, bright: 0, pointerAngle: null, proximityT: 0 };
  });

  const onPointerMove = (event) => {
    for (const inst of instances) {
      const rect = inst.btn.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = Math.max(rect.left - event.clientX, 0, event.clientX - rect.right);
      const dy = Math.max(rect.top - event.clientY, 0, event.clientY - rect.bottom);
      const dist = Math.hypot(dx, dy);

      if (dist === 0) {
        // Over the button itself: settle on the diagonal, sway gently with the cursor.
        const nx = (event.clientX - cx) / (rect.width / 2);
        const ny = (cy - event.clientY) / (rect.height / 2);
        inst.pointerAngle = Math.atan2(2 / rect.height, -2 / rect.width) + nx * 0.3 + ny * 0.15;
      } else {
        inst.pointerAngle = Math.atan2(cy - event.clientY, event.clientX - cx);
      }

      const t = Math.max(0, 1 - dist / PROXIMITY);
      inst.proximityT = t * t * (3 - 2 * t); // smoothstep
    }
  };
  window.addEventListener('pointermove', onPointerMove, { passive: true });

  let last = performance.now();
  function raf(now) {
    requestAnimationFrame(raf);
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;

    for (const inst of instances) {
      inst.idleAngle += IDLE_SPEED * dt;
      const target = inst.pointerAngle != null ? inst.pointerAngle : inst.idleAngle;
      const diff = ((target - inst.angle + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      inst.angle += diff * (1 - Math.exp(-dt * 7));
      inst.bright += (inst.proximityT - inst.bright) * (1 - Math.exp(-dt * 8));

      inst.fx.style.setProperty('--sb-angle', `${(inst.angle * 180) / Math.PI}deg`);
      inst.fx.style.setProperty('--sb-bright', inst.bright.toFixed(3));
    }
  }
  requestAnimationFrame(raf);
})();
